import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";
import type { SliceStateCreator } from "../../types";
import { initialChatRoomState, initialChatRoomUiState } from "./constants";
import type { ChatRoomActionsSliceState } from "./types";

export const createChatRoomActionsSlice: SliceStateCreator<
  ChatRoomActionsSliceState
> = (set, get) => ({
  actions: {
    createNewChatRoom: uuid => {
      set(state => {
        state.chatRoomStateMap.set(uuid, { ...initialChatRoomState });
      });
      return initialChatRoomUiState;
    },
    fetchChatRoom: async (uuid: string) => {
      const chatRoomState =
        get().chatRoomStateMap.get(uuid) || initialChatRoomState;
      const chatRoomUiState = chatRoomState?.uiState || initialChatRoomUiState;
      const newChatRoomState = {
        ...chatRoomState,
        uiState: {
          ...chatRoomUiState,
          isLoading: true,
        },
      };
      set(state => {
        state.chatRoomStateMap.set(uuid, newChatRoomState);
      });
      try {
        const data = await reactQueryGet<{ chat_room: ChatRoom }>(
          internalApi.chatRooms.show({ uuid }),
        );
        if (data.chat_room) {
          set(state => {
            const chatRoom = data.chat_room;
            state.chatRoomMap.set(uuid, chatRoom);
            const chatRoomState = state.chatRoomStateMap.get(uuid);
            if (chatRoomState) {
              chatRoomState.uiState.isLoading = false;
            }
          });
        }
      } catch (error) {
        set(state => {
          const chatRoomState = state.chatRoomStateMap.get(uuid);
          if (chatRoomState) {
            chatRoomState.uiState.isLoading = false;
          }
        });
        console.error(error);
      }
    },
    ensureChatRoomParticipant: async (uuid: string) => {
      const chatRoom = get().chatRoomMap.get(uuid);
      const currentParticipant = chatRoom?.current_participant;
      if (!currentParticipant) {
        await get().actions.fetchChatRoom(uuid);
      }
    },
    updateChatRoom: (chatRoom: Partial<ChatRoom> & { uuid: string }) =>
      set(state => {
        const currentChatRoom = get().chatRoomMap.get(chatRoom.uuid);
        if (currentChatRoom) {
          state.chatRoomMap.set(chatRoom.uuid, {
            ...currentChatRoom,
            ...chatRoom,
          });
        }
      }),
    appendChatRoom: (chatRoom: ChatRoom) =>
      set(state => {
        state.chatRoomMap.delete(chatRoom.uuid);
        state.chatRoomMap.set(chatRoom.uuid, chatRoom);
      }),
    removeChatRoom: (uuid: string) =>
      set(state => {
        state.chatRoomMap.delete(uuid);
      }),
    markChatRoomAsRead: (uuid: string) =>
      set(state => {
        const chatRoom = state.chatRoomMap.get(uuid);
        if (chatRoom) {
          chatRoom.has_unread_messages = false;
        }
      }),
  },
});
